import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AppLayout, EmptyState, ErrorBanner, PageHeader } from '@/components/AppLayout'
import { EventbriteEventPicker } from '@/features/events/EventbriteEventPicker'
import { useAuth } from '@/features/auth/useAuth'
import { useAsync } from '@/hooks/useAsync'
import { createEvent, listEvents } from '@/lib/queries'
import { supabase } from '@/lib/supabase'
import type { Event } from '@/types/database'

function formatDate(value: string | null): string {
  if (value === null) return 'No date set'
  return new Date(value).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const STATUS_BADGE: Record<Event['status'], string> = {
  draft: 'badge',
  active: 'badge badge-accent',
  completed: 'badge badge-success',
  archived: 'badge',
}

export function EventsListPage() {
  const { memberships, refreshMemberships } = useAuth()
  const events = useAsync(() => listEvents(), [])
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [name, setName] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const organization = memberships[0]?.organization

  async function handleCreateOrganization() {
    setBusy(true)
    setActionError(null)
    try {
      const { error } = await supabase.rpc('create_organization', { p_name: 'My organization' })
      if (error) throw new Error(error.message)
      await refreshMemberships()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not create the organization')
    } finally {
      setBusy(false)
    }
  }

  async function handleCreateEvent() {
    if (!organization || name.trim() === '') return

    setBusy(true)
    setActionError(null)
    try {
      await createEvent({
        organizationId: organization.id,
        name: name.trim(),
        startsAt: startsAt === '' ? null : new Date(startsAt).toISOString(),
      })
      setName('')
      setStartsAt('')
      setCreating(false)
      events.reload()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not create the event')
    } finally {
      setBusy(false)
    }
  }

  // A brand new admin has no organization yet; everything else depends on one.
  if (memberships.length === 0) {
    return (
      <AppLayout>
        <div className="page">
          <PageHeader title="Welcome" />
          <div className="card">
            <EmptyState
              title="Create your organization"
              description="Events, attendees and results all belong to an organization. You can rename it later in settings."
              action={
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => void handleCreateOrganization()}
                  disabled={busy}
                >
                  {busy ? 'Creating…' : 'Create organization'}
                </button>
              }
            />
            {actionError !== null && <ErrorBanner message={actionError} />}
          </div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <div className="page">
        <PageHeader
          title="Events"
          breadcrumb={organization?.name}
          actions={
            <>
              <button
                className="btn"
                type="button"
                onClick={() => {
                  setImporting((open) => !open)
                  setCreating(false)
                }}
              >
                {importing ? 'Cancel' : 'Import from Eventbrite'}
              </button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  setCreating((open) => !open)
                  setImporting(false)
                }}
              >
                {creating ? 'Cancel' : 'New event'}
              </button>
            </>
          }
        />

        {actionError !== null && <ErrorBanner message={actionError} />}
        {events.error !== null && <ErrorBanner message={events.error} />}

        {importing && organization !== undefined && (
          <EventbriteEventPicker
            organizationId={organization.id}
            linkedEventbriteIds={
              new Set(
                (events.data ?? [])
                  .map((e) => e.eventbrite_event_id)
                  .filter((id): id is string => id !== null)
              )
            }
            onImported={() => {
              events.reload()
            }}
            onClose={() => {
              setImporting(false)
            }}
          />
        )}

        {creating && (
          <form
            className="card"
            onSubmit={(e) => {
              e.preventDefault()
              void handleCreateEvent()
            }}
          >
            <h2>New event</h2>
            <div className="field">
              <label className="field-label" htmlFor="event-name">
                Event name
              </label>
              <input
                id="event-name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                }}
                placeholder="Thursday Social"
                required
                autoFocus
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="event-date">
                Starts
              </label>
              <input
                id="event-date"
                type="datetime-local"
                value={startsAt}
                onChange={(e) => {
                  setStartsAt(e.target.value)
                }}
              />
            </div>
            <div className="row btn-group-stack">
              <button
                className="btn btn-primary"
                type="submit"
                disabled={busy || name.trim() === ''}
              >
                {busy ? 'Creating…' : 'Create event'}
              </button>
            </div>
          </form>
        )}

        {events.loading ? (
          <p className="page-status">Loading events…</p>
        ) : (events.data ?? []).length === 0 ? (
          <div className="card">
            <EmptyState
              title="No events yet"
              description="Import one from Eventbrite, or create it by hand. Either way you can then import attendees and give each one a nametag number."
            />
          </div>
        ) : (
          <div className="table-responsive">
            <div className="table-wrap">
              <table>
                <caption className="sr-only">Your events</caption>
                <thead>
                  <tr>
                    <th scope="col">Event</th>
                    <th scope="col">Date</th>
                    <th scope="col">Status</th>
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(events.data ?? []).map((event) => (
                    <tr key={event.id}>
                      <td data-label="Event">
                        <Link to={`/events/${event.id}`}>{event.name}</Link>
                      </td>
                      <td data-label="Date" className="cell-muted">
                        {formatDate(event.starts_at)}
                      </td>
                      <td data-label="Status">
                        <span className={STATUS_BADGE[event.status]}>{event.status}</span>
                      </td>
                      <td data-label="">
                        <Link className="btn btn-sm" to={`/events/${event.id}`}>
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
